import { For, onCleanup, onMount, Show } from 'solid-js'
import {
  editingTextId,
  liveStroke,
  showGrid,
  setViewportSize,
  spaceDown,
} from '../editor.ts'
import { scene, setCamera } from '../scene/store.ts'
import { currentCursor, currentTool } from '../tools/index.ts'
import { startPan } from '../tools/pan.ts'
import { polylineD, smoothStrokeD } from '../util/path.ts'
import { panByScreen, worldTransform, zoomAt } from './camera.ts'
import { NodeView } from './nodes/NodeView.tsx'
import { SelectionOverlay } from './Selection.tsx'
import {
  clientToScreen,
  clientToWorld,
  refreshViewportRect,
  setViewportEl,
} from './viewportRef.ts'
import type { PointerCtx } from '../tools/types.ts'

const WHEEL_ZOOM_RATE = 0.0015

/** Grid spacing in world units, kept between 16 and 64 screen px. */
function gridStep(zoom: number): number {
  let step = 32
  while (step * zoom < 16) step *= 2
  while (step * zoom > 64) step /= 2
  return step
}

function isDark(color: string): boolean {
  const m = /^#([0-9a-f]{6})/i.exec(color)
  if (!m) return false
  const n = parseInt(m[1]!, 16)
  const luma = 0.299 * (n >> 16) + 0.587 * ((n >> 8) & 0xff) + 0.114 * (n & 0xff)
  return luma < 128
}

export function Viewport() {
  let el!: HTMLDivElement

  const onWheel = (e: WheelEvent) => {
    e.preventDefault()
    refreshViewportRect()
    // deltaMode 1 is lines, 2 is pages; normalize both to pixels.
    const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? el.clientHeight : 1
    const dx = e.deltaX * unit
    const dy = e.deltaY * unit

    if (e.shiftKey) {
      setCamera(panByScreen(scene.camera, -(dx || dy), 0))
      return
    }
    setCamera(zoomAt(scene.camera, clientToScreen(e), Math.exp(-dy * WHEEL_ZOOM_RATE)))
  }

  onMount(() => {
    setViewportEl(el)
    const ro = new ResizeObserver(() => {
      refreshViewportRect()
      setViewportSize({ w: el.clientWidth, h: el.clientHeight })
    })
    ro.observe(el)
    // Must be non-passive: zooming has to cancel the browser's own scroll.
    el.addEventListener('wheel', onWheel, { passive: false })

    onCleanup(() => {
      ro.disconnect()
      el.removeEventListener('wheel', onWheel)
      setViewportEl(null)
    })
  })

  const buildCtx = (e: PointerEvent): PointerCtx => {
    const hit = (e.target as Element | null)?.closest('[data-node-id]')
    return {
      event: e,
      screen: clientToScreen(e),
      world: clientToWorld(e),
      nodeId: hit?.getAttribute('data-node-id') ?? null,
    }
  }

  const onPointerDown = (e: PointerEvent) => {
    refreshViewportRect()

    if (e.button === 1 || (e.button === 0 && spaceDown())) {
      e.preventDefault()
      startPan(e)
      return
    }
    if (e.button === 2) {
      currentTool().onSecondaryDown?.(buildCtx(e))
      return
    }
    if (e.button !== 0) return

    const ctx = buildCtx(e)

    // Clicking away from an open text editor has to commit it; the viewport is
    // not focusable, so the blur would otherwise never fire.
    const open = editingTextId()
    if (open && ctx.nodeId !== open) {
      ;(document.activeElement as HTMLElement | null)?.blur()
    }

    if (!editingTextId()) e.preventDefault()
    currentTool().onPointerDown(ctx)
  }

  const onDblClick = (e: MouseEvent) => {
    const tool = currentTool()
    if (!tool.onDoubleClick) return
    tool.onDoubleClick(buildCtx(e as unknown as PointerEvent))
  }

  const gridStyle = () => {
    const dot = isDark(scene.background)
      ? 'rgba(255, 255, 255, 0.24)'
      : 'rgba(0, 0, 0, 0.2)'
    const { x, y, z } = scene.camera
    const step = gridStep(z) * z
    // One dot as a tiled image, not a tiled radial-gradient: software raster
    // redraws a gradient per cell, which stalled every frame on a busy canvas.
    const tile =
      `<svg xmlns="http://www.w3.org/2000/svg" width="${step}" height="${step}">` +
      `<circle cx="${step / 2}" cy="${step / 2}" r="0.75" fill="${dot}"/></svg>`
    return {
      'background-color': scene.background,
      'background-image': showGrid() ? `url("data:image/svg+xml,${encodeURIComponent(tile)}")` : 'none',
      'background-size': `${step}px ${step}px`,
      'background-position': `${-x * z}px ${-y * z}px`,
    }
  }

  return (
    <div
      ref={el}
      class="viewport"
      style={{ ...gridStyle(), cursor: currentCursor() }}
      onPointerDown={onPointerDown}
      onDblClick={onDblClick}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div class="world" style={{ transform: worldTransform(scene.camera) }}>
        <For each={scene.order}>
          {(id) => (
            <Show when={scene.nodes[id]}>{(node) => <NodeView node={node()} />}</Show>
          )}
        </For>

        <Show when={liveStroke()}>
          {(s) => (
            <svg class="live-stroke" width="1" height="1" overflow="visible">
              <path
                d={
                  s().polyline
                    ? polylineD(s().points, s().closed)
                    : smoothStrokeD(s().points)
                }
                fill="none"
                stroke={s().stroke}
                stroke-width={s().strokeWidth}
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <Show when={s().closeAt}>
                {(p) => (
                  <circle
                    class="close-target"
                    cx={p().x}
                    cy={p().y}
                    r={7 / scene.camera.z}
                    stroke-width={1.5 / scene.camera.z}
                  />
                )}
              </Show>
            </svg>
          )}
        </Show>
      </div>

      <SelectionOverlay />
    </div>
  )
}
